<script setup>
import { computed, nextTick, ref } from 'vue';

const props = defineProps({
  modelValue: { type: [String, Number], default: 'fotos' },
  ariaLabel: { type: String, default: 'Galeria' },
  idBase: { type: String, default: '' },
  tabs: {
    type: Array,
    default: () => [
      { id: 'fotos', label: 'Fotos' },
      { id: 'video', label: 'Vídeos' },
      { id: 'video360', label: 'Tour 360°' },
      { id: 'planta', label: 'Planta' },
    ],
    validator: value =>
      value.every(
        tab => tab && typeof tab.id !== 'undefined' && 'label' in tab
      ),
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const generatedIdBase = `lais-gallery-tabs-${Math.random()
  .toString(36)
  .slice(2, 8)}`;
const tabRefs = ref([]);

const resolvedIdBase = computed(() => props.idBase || generatedIdBase);
const enabledTabs = computed(() => props.tabs.filter(tab => !tab.disabled));

const activeId = computed(() => {
  const current = props.tabs.find(
    tab => String(tab.id) === String(props.modelValue)
  );
  if (current && !current.disabled) return current.id;
  return enabledTabs.value[0]?.id ?? '';
});

const isActive = id => String(id) === String(activeId.value);
const tabId = id => `${resolvedIdBase.value}-tab-${id}`;
const panelId = id => `${resolvedIdBase.value}-panel-${id}`;

const selectTab = id => {
  const target = props.tabs.find(tab => String(tab.id) === String(id));
  if (!target || target.disabled) return;
  if (String(target.id) !== String(props.modelValue)) {
    emit('update:modelValue', target.id);
    emit('change', target.id);
  }
};

const focusTab = id => {
  const domIndex = props.tabs.findIndex(tab => String(tab.id) === String(id));
  const el = tabRefs.value[domIndex];
  if (el) el.focus();
};

const enabledIndexOf = id =>
  enabledTabs.value.findIndex(tab => String(tab.id) === String(id));

const moveFocus = (fromId, delta) => {
  const list = enabledTabs.value;
  if (!list.length) return;

  const currentIndex = Math.max(0, enabledIndexOf(fromId));
  const nextIndex = (currentIndex + delta + list.length) % list.length;
  const nextTab = list[nextIndex];
  selectTab(nextTab.id);
  nextTick(() => focusTab(nextTab.id));
};

const onKeydown = (event, tab) => {
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      event.preventDefault();
      moveFocus(tab.id, 1);
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      event.preventDefault();
      moveFocus(tab.id, -1);
      break;
    case 'Home':
      event.preventDefault();
      if (enabledTabs.value[0]) {
        selectTab(enabledTabs.value[0].id);
        nextTick(() => focusTab(enabledTabs.value[0].id));
      }
      break;
    case 'End': {
      event.preventDefault();
      const last = enabledTabs.value[enabledTabs.value.length - 1];
      if (last) {
        selectTab(last.id);
        nextTick(() => focusTab(last.id));
      }
      break;
    }
    case 'Enter':
    case ' ':
      event.preventDefault();
      selectTab(tab.id);
      break;
    default:
      break;
  }
};
</script>

<template>
  <div
    role="tablist"
    :aria-label="ariaLabel || undefined"
    class="flex flex-wrap gap-2"
  >
    <button
      v-for="tab in tabs"
      :id="tabId(tab.id)"
      :key="tab.id"
      ref="tabRefs"
      type="button"
      role="tab"
      :aria-selected="isActive(tab.id) ? 'true' : 'false'"
      :aria-controls="panelId(tab.id)"
      :aria-disabled="tab.disabled ? 'true' : undefined"
      :tabindex="isActive(tab.id) ? 0 : -1"
      :disabled="tab.disabled"
      class="inline-flex h-10 items-center rounded-full px-4 text-[13px] font-semibold transition-colors"
      :class="
        isActive(tab.id)
          ? 'bg-lais-primary text-white'
          : 'bg-white text-lais-text-muted hover:bg-lais-primary-50 disabled:cursor-not-allowed disabled:opacity-50'
      "
      @click="selectTab(tab.id)"
      @keydown="onKeydown($event, tab)"
    >
      {{ tab.label }}
    </button>
  </div>
</template>
